import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon, UploadFile as UploadFileIcon } from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useLogos from "../../hooks/useLogos";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const blank = {
    legacy_id: "",
    name: "",
    label_de: "",
    sort_order: 0,
    is_active: true,
};

function resolveUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value) || value.startsWith("blob:")) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

function slugify(value) {
    return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function LogosPage() {
    const { logos, loading, error, fetchLogos, createLogo, updateLogo, deleteLogo } = useLogos();
    const [search, setSearch] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(blank);
    const [editing, setEditing] = React.useState(null);
    const [imageFile, setImageFile] = React.useState(null);
    const [imagePreview, setImagePreview] = React.useState("");
    const [submitError, setSubmitError] = React.useState("");
    const [submitting, setSubmitting] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    React.useEffect(() => {
        return () => {
            if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    const rows = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = [...(logos || [])].sort((a, b) => (
            Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
            String(a.label_de || a.name || "").localeCompare(String(b.label_de || b.name || ""), "de")
        ));
        if (!q) return list;
        return list.filter((logo) => (
            String(logo.legacy_id || "").includes(q) ||
            String(logo.name || "").toLowerCase().includes(q) ||
            String(logo.label_de || "").toLowerCase().includes(q)
        ));
    }, [logos, search]);

    const setPickedFile = (file) => {
        if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
        setImageFile(file || null);
        setImagePreview(file ? URL.createObjectURL(file) : "");
    };

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setPickedFile(null);
        setSubmitError("");
        setOpen(true);
    };

    const openEdit = (logo) => {
        setEditing(logo);
        setForm({
            ...blank,
            ...logo,
            legacy_id: logo.legacy_id ?? "",
            sort_order: logo.sort_order ?? 0,
            is_active: logo.is_active !== undefined ? !!logo.is_active : true,
        });
        setPickedFile(null);
        setImagePreview(resolveUrl(logo.url));
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {
        const label = (form.label_de || "").trim();
        const name = (form.name || slugify(label)).trim();
        if (!label) {
            setSubmitError("Label ist Pflichtfeld.");
            return;
        }
        if (!name) {
            setSubmitError("Key/Name ist Pflichtfeld.");
            return;
        }

        const payload = {
            legacy_id: form.legacy_id === "" ? null : Number(form.legacy_id),
            name,
            label_de: label,
            sort_order: Number(form.sort_order || 0),
            is_active: !!form.is_active,
        };

        setSubmitting(true);
        setSubmitError("");
        try {
            if (editing) await updateLogo(editing.id, payload, imageFile);
            else await createLogo(payload, imageFile);
            setOpen(false);
        } catch (e) {
            setSubmitError(e?.response?.data?.error || e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageContainer
            title="Logos"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchLogos} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={openCreate}>
                        Neues Logo
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Logos suchen"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: "70vh", overflow: "auto" }}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Alte ID</TableCell>
                                <TableCell>Logo</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>Aktiv</TableCell>
                                <TableCell>Sortierung</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((logo) => (
                                <TableRow key={logo.id} hover>
                                    <TableCell>{logo.legacy_id ?? "-"}</TableCell>
                                    <TableCell>
                                        {logo.url ? (
                                            <Box component="img" src={resolveUrl(logo.url)} alt={logo.label_de} sx={{ maxWidth: 96, maxHeight: 46, objectFit: "contain" }} />
                                        ) : (
                                            <Alert severity="info" icon={false} sx={{ py: 0 }}>Kein Bild</Alert>
                                        )}
                                    </TableCell>
                                    <TableCell>{logo.label_de || logo.name}</TableCell>
                                    <TableCell>{logo.is_active ? "Ja" : "Nein"}</TableCell>
                                    <TableCell>{logo.sort_order ?? 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => openEdit(logo)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => { setDeleteTarget(logo); setDeleteError(""); }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? "Logo bearbeiten" : "Neues Logo anlegen"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gap: 2 }}>
                        <TextField label="Alte ID" type="number" value={form.legacy_id} onChange={(e) => setForm((f) => ({ ...f, legacy_id: e.target.value }))} />
                        <TextField label="Label*" value={form.label_de} onChange={(e) => setForm((f) => ({ ...f, label_de: e.target.value, name: f.name || slugify(e.target.value) }))} />
                        <TextField label="Key/Name*" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: slugify(e.target.value) }))} />
                        <TextField label="Sortierung" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
                            label="Aktiv"
                        />

                        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                            Bild hochladen / ersetzen
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    setPickedFile(e.target.files?.[0] || null);
                                    e.target.value = "";
                                }}
                            />
                        </Button>

                        {imagePreview && (
                            <Paper variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 120 }}>
                                <Box component="img" src={imagePreview} alt="Vorschau" sx={{ maxWidth: "100%", maxHeight: 110, objectFit: "contain" }} />
                            </Paper>
                        )}
                    </Box>
                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern..." : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Logo löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du <b>{deleteTarget?.label_de || deleteTarget?.name}</b> wirklich löschen?
                            Zuordnungen zu Firmen werden automatisch entfernt.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await deleteLogo(deleteTarget.id);
                                setDeleteTarget(null);
                            } catch (e) {
                                setDeleteError(e?.response?.data?.error || e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen..." : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
